interface FhrsRatingDetail {
  name: string;
  alt: string;
}

type FhrsMapValue = FhrsRatingDetail | string;

/** Resolved food-hygiene badge image (produced by getFoodHygieneRatingBadge). */
export interface HygieneRatingBadge {
  src: string;
  alt: string;
}

const normalizeRating = (rating: string): string => {
  return rating.toLowerCase().replaceAll(/\s+/g, '_');
};

const getAltText = (rating: FhrsMapValue): string => {
  const MAX_RATING = 5;
  const baseText = `Food Hygiene Rating: `;
  if (typeof rating === 'object') {
    return `${baseText}${rating.name} out of ${MAX_RATING}, ${rating.alt}`;
  }
  return `${baseText}${rating}`;
};

const FHRS_MAP: Record<string, FhrsMapValue> = {
  0: { name: '0', alt: 'Urgent Improvement Necessary' },
  1: { name: '1', alt: 'Major Improvement Necessary' },
  2: { name: '2', alt: 'Improvement Necessary' },
  3: { name: '3', alt: 'Generally Satisfactory' },
  4: { name: '4', alt: 'Good' },
  5: { name: '5', alt: 'Very Good' },
  exempt: 'exempt',
  awaiting_publication: 'awaitingpublication',
  awaiting_inspection: 'awaitinginspection',
};

const FHIS_MAP: Record<string, string> = {
  awaiting_inspection: 'awaiting_inspection',
  awaiting_publication: 'awaiting_publication',
  exempt: 'exempt',
  improvement_required: 'improvement_required',
  pass_and_eat_safe: 'pass_and_eat_safe',
  pass: 'pass',
};

export const getFoodHygieneRatingBadge = async (
  rating: string | null | undefined,
  country: string | null | undefined
): Promise<HygieneRatingBadge | null> => {
  if (!country || rating == null) return null;

  if (country.toLowerCase() === 'england') {
    const _rating = FHRS_MAP[normalizeRating(rating)];
    if (!_rating) return null;

    const ratingKey = typeof _rating === 'object' ? _rating.name : _rating;
    const src = await import(`./fhrs/fhrs_${ratingKey}_en-gb.svg`);
    return {
      src: src.default,
      alt: getAltText(_rating),
    };
  }
  if (country.toLowerCase() === 'scotland') {
    const _rating = FHIS_MAP[normalizeRating(rating)];
    if (!_rating) return null;

    const src = await import(`./fhis/fhis_${_rating}.jpg`);
    return {
      src: src.default,
      alt: getAltText(_rating),
    };
  }

  return null;
};
